import { AxiosResponse } from 'axios';

import { useQueryClient } from '@tanstack/react-query';

import { TypeRequestHeaders, axiosInstance } from '~/services/fetchServiceV2';
import type {
  TypeCodeState,
  TypeContract,
  TypeSafetyEducation,
  TypeSpecialMedicalExamination,
  TypeUserSubscribe,
} from '~/types';
import { TypeErrorFormat, requestHelper } from '~/utils/fetchHelper';

import type { TypePrivacyInfo } from './useAgreeContractTerms';

/** @category Login */
export type TypeUserInfoResponse = {
  code: string;
  message: string;
  data: {
    /** 계정 코드 */
    laborAccountId: number;
    /** 로그인 아이디 */
    loginId: string;
    /** 이름 */
    name: string;
    /** 작업자 계정 상태 */
    status: TypeCodeState;
    /** 계약 구분 */
    contractType: TypeContract;
    /** 휴대전화 번호 */
    phoneNumber: string;
    /** 상용직 정보 */
    kurlyContract: {
      /** 클러스터 */
      cluster: TypeCodeState;
      /** 센터 */
      center: TypeCodeState;
      /** 업무 파트 */
      workPart: TypeCodeState;
    } | null;
    /** 최근 출근 정보 */
    latestCommute: {
      /** 클러스터 */
      cluster: TypeCodeState;
      /** 센터 */
      center: TypeCodeState;
    } | null;
    /** 계약 시 입력한 개인정보 */
    privacyInfo: TypePrivacyInfo | null;
    /** 근로 계약 가능 여부 */
    contractAvailableStatus: TypeCodeState | null;
    /** 근로 계약 일시  */
    latestContractSignedAt: string | null;
    /** 작업자 수신동의 */
    subscribe: TypeUserSubscribe | null;
    /** 현재 근로계약상태 */
    currentContractStatus: TypeCodeState | null;
    /** 본인인증 상태 */
    certificationStatus: TypeCodeState | null;
    /** 신규안전교육 상태 */
    safetyEducation: TypeSafetyEducation;
    /** 초기 비밀번호 사용 여부 */
    initPassword: boolean;
    /** 비밀번호 변경 필요 여부 */
    passwordChangeRequired: boolean;
    /** 특수건강검진 정보 */
    specialMedicalExamination: TypeSpecialMedicalExamination | null;
    /** 재동의 필요 여부 */
    reConsentRequired: boolean;
  };
};

/**
 * @description 계정정보 조회 api 요청에 사용되는 커스텀 훅
 */
export const useUserInfo = () => {
  const queryClient = useQueryClient();

  const handleSuccess = (data: TypeUserInfoResponse) => {
    return data?.data ?? null;
  };

  const requestUserInfo = async ({
    onSuccess,
    onError,
    onFinally,
    headers,
    showLoading = true,
  }: {
    onSuccess?: (data: TypeUserInfoResponse['data']) => void;
    onError?: (error: TypeErrorFormat) => void;
    onFinally?: () => void;
    headers?: TypeRequestHeaders;
    showLoading?: boolean;
  }) => {
    const endpoint = '/labor-account';
    const query = queryClient.ensureQueryData<AxiosResponse<TypeUserInfoResponse>>({
      queryKey: ['labor-account'],
      queryFn: () => axiosInstance.get(endpoint, { headers }),
      gcTime: 0,
    });

    return requestHelper(
      () => query,
      (response) => onSuccess?.(handleSuccess(response)),
      onError,
      onFinally,
      { showLoading }
    );
  };

  return requestUserInfo;
};
